import { CSSProperties } from 'react';
import { useReduxState } from 'hooks';
import { ArrayField } from 'informed';
import { attributesExpressionOptions } from 'lib/customAttributes';
import { Button } from 'components/common';
import { FormGroup, Select, Text } from 'components/common/form';

import type { UserCustomAttribute } from 'shared-types';

export type UserAttributesConditionsProps = {
  availableAttributes: {
    [key: string]: Pick<
      UserCustomAttribute,
      'max' | 'min' | 'title' | 'type' | 'optionGroups'
    >;
  };
  disabled?: boolean;
  fieldPrefix?: string;
  includeAuthenticationTypeCondition?: boolean;
  includeEmailCondition?: boolean;
  includeEmailDomainCondition?: boolean;
  includeIsSelfRegisteredCondition?: boolean;
  label?: string;
  labelHelpText?: string;
  style?: CSSProperties;
};

const UserAttributesConditions = ({
  availableAttributes,
  disabled = false,
  fieldPrefix = '',
  includeAuthenticationTypeCondition = false,
  includeEmailCondition = false,
  includeEmailDomainCondition = false,
  includeIsSelfRegisteredCondition = false,
  label,
  labelHelpText,
  style
}: UserAttributesConditionsProps) => {
  const { messages, theme } = useReduxState();

  const initialStateByType = (key?: string) => {
    let definedKey = key;
    if (!definedKey) definedKey = Object.keys(attributes)[0];

    const attribute = attributes[
      definedKey
    ] as UserAttributesConditionsProps['availableAttributes']['key'];

    return {
      key: definedKey,
      expression:
        attribute.type === 'Array' || attribute.type === 'ArrayWithOptions'
          ? 'includes'
          : 'equals',
      value:
        attribute.type === 'Boolean'
          ? true
          : attribute.type === 'Number'
          ? attribute.min
          : attribute.optionGroups?.length
          ? attribute.optionGroups[0].options[0].key
          : ''
    };
  };

  const handleAttributeChange = (
    value: string,
    setValue: (field: string, value: any) => void
  ) => {
    const initialState = initialStateByType(value);

    setValue('expression', initialState.expression);
    setValue('value', initialState.value);
  };

  const attributes = {
    ...(includeEmailCondition
      ? { email: { title: messages.commonEmailAddress, type: 'String' } }
      : {}),
    ...(includeEmailDomainCondition
      ? { emailDomain: { title: 'Email domain', type: 'String' } }
      : {}),
    ...(includeAuthenticationTypeCondition
      ? {
          authenticationType: {
            title: 'Authentication type',
            type: 'StringWithOptions'
          }
        }
      : {}),
    ...(includeIsSelfRegisteredCondition
      ? {
          isSelfRegistered: {
            title: 'Is self-registered',
            type: 'Boolean'
          }
        }
      : {}),
    ...availableAttributes
  } as { [attr: string]: (typeof availableAttributes)['0'] };

  const optionsByAttribute: {
    [key: string]: { key: string; label: string }[];
  } = {
    authenticationType: [
      { key: 'local', label: 'Local' },
      { key: 'SSO', label: 'SSO' }
    ]
  };

  Object.keys(availableAttributes).forEach((attributeKey) => {
    const optionGroups = attributes[attributeKey].optionGroups || [];

    const allOptions = optionGroups.reduce((allOptions, group) => {
      if (!group.options?.length) return;
      allOptions.push(...(group.options || []));
      return allOptions;
    }, [] as { key: string; label: string }[]);

    optionsByAttribute[attributeKey] = allOptions;
  });

  const hasNoValidAttributes = !Object.keys(attributes).length;

  return (
    <FormGroup
      label={label !== undefined ? label : messages.userAttributesConditions}
      labelHelpText={labelHelpText}
      maxWidth="100%"
      style={style}
    >
      <ArrayField field={`${fieldPrefix ? fieldPrefix + '.' : ''}conditions`}>
        {({ addWithInitialValue }) => (
          <>
            <ArrayField.Items<{ expression: string; key: string }>>
              {({ remove, field, index, values, setValue }) => {
                const { expression = '', key = '' } = values || {};
                const selectedAttribute = attributes[key];

                return (
                  <div className="condition">
                    <div className="condition-pre">
                      {messages.userAttributesOnlyIf}
                    </div>

                    <div className="condition-key">
                      <Select
                        disabled={disabled}
                        field={`${field}.key`}
                        id={`${fieldPrefix}-condition-key-${index}`}
                        onChange={(value: string) =>
                          handleAttributeChange(value, setValue)
                        }
                        options={Object.keys(attributes).map((attr) => ({
                          label: attributes[attr].title,
                          value: attr
                        }))}
                      />
                    </div>

                    <div className="condition-post">
                      <Select
                        disabled={disabled}
                        field={`${field}.expression`}
                        id={`${fieldPrefix}-condition-expression-${index}`}
                        options={attributesExpressionOptions({
                          messages,
                          type: selectedAttribute?.type
                        })}
                        style={{ maxWidth: 150 }}
                      />
                    </div>

                    {['any', 'empty'].includes(expression as string) ? null : (
                      <div className="condition-value">
                        {selectedAttribute?.type === 'Boolean' ? (
                          <Select
                            disabled={disabled}
                            field={`${field}.value`}
                            id={`${fieldPrefix}-condition-value-boolean-${index}`}
                            options={[
                              { label: messages.commonYes, value: true },
                              { label: messages.commonNo, value: false }
                            ]}
                          />
                        ) : selectedAttribute?.type === 'Number' ? (
                          <Select
                            disabled={disabled}
                            field={`${field}.value`}
                            id={`${fieldPrefix}-condition-value-number-${index}`}
                            isSearchable={false}
                            options={Array.from(
                              Array(
                                Math.max(
                                  selectedAttribute.max -
                                    selectedAttribute.min +
                                    1,
                                  1
                                )
                              ),
                              (_, i) => ({
                                label: i + selectedAttribute.min,
                                value: i + selectedAttribute.min
                              })
                            )}
                          />
                        ) : selectedAttribute?.type === 'ArrayWithOptions' ||
                          selectedAttribute?.type === 'StringWithOptions' ? (
                          <Select
                            disabled={disabled}
                            field={`${field}.value`}
                            id={`${fieldPrefix}-condition-value-options-${index}`}
                            isMulti={
                              values?.expression === 'is one of' ||
                              values?.expression === 'includes one of'
                            }
                            isSearchable={true}
                            options={optionsByAttribute[
                              values?.key as string
                            ].map((option) => ({
                              label: option.label,
                              value: option.key
                            }))}
                          />
                        ) : (selectedAttribute?.type === 'String' ||
                            selectedAttribute?.type === 'Array') &&
                          (expression === 'is one of' ||
                            expression === 'includes one of') ? (
                          <Select
                            disabled={disabled}
                            field={`${field}.value`}
                            id={`${fieldPrefix}-condition-value-options-${index}`}
                            creatable={true}
                            isMulti={true}
                            isSearchable={true}
                            style={{ minWidth: 150 }}
                          />
                        ) : (
                          <Text
                            disabled={disabled}
                            field={`${field}.value`}
                            id={`${fieldPrefix}-condition-value-text-${index}`}
                            required={true}
                            validateOnChange={true}
                          />
                        )}
                      </div>
                    )}

                    <div className="condition-actions">
                      <Button
                        aria-label={messages.commonDelete}
                        category="light"
                        data-tooltip={messages.commonDelete}
                        disabled={disabled}
                        icon="trash-alt"
                        onClick={remove}
                        size="small"
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </div>
                );
              }}
            </ArrayField.Items>

            <Button
              category="light"
              disabled={disabled || hasNoValidAttributes}
              icon="plus"
              onClick={() => addWithInitialValue(initialStateByType())}
              size="small"
              style={{ marginTop: 5 }}
              text={messages.userAttributesConditionsAdd}
            />
          </>
        )}
      </ArrayField>

      <style jsx>{`
        .condition {
          align-items: flex-start;
          display: flex;
          flex-wrap: wrap;
          gap: 0 10px;
        }
        .condition:not(:first-child) {
          margin-top: 5px;
        }
        .condition:last-of-type {
          margin-bottom: 5px;
        }
        .condition-pre {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
          margin: 9px 0;
          white-space: nowrap;
        }
        .condition-key {
          min-width: 120px;
        }
        .condition-post {
          flex-shrink: 0;
          min-width: 120px;
        }
      `}</style>
    </FormGroup>
  );
};

export default UserAttributesConditions;
